<template>
	<v-card outlined class="trained-face-capture-panel pa-3">
		<div
			v-if="source === 'upload'"
			class="trained-face-capture-panel__dropzone"
			@dragover.prevent
			@drop.prevent="handleDrop"
		>
			<v-file-input
				dense
				outlined
				multiple
				accept="image/*"
				:loading="loading"
				:label="label"
				@change="emitFiles"
			></v-file-input>
			<div class="caption grey--text text--darken-1 text-center">
				Drop image files here or choose files from your device.
			</div>
		</div>

		<div v-else-if="isBrowserCameraSource" class="trained-face-capture-panel__browser-camera">
			<video
				ref="video"
				class="trained-face-capture-panel__video"
				autoplay
				muted
				playsinline
			></video>
			<v-alert v-if="cameraNotice" dense outlined :type="captureError ? 'warning' : 'info'" class="trained-face-capture-panel__fallback mt-2">
				{{ cameraNotice }}
			</v-alert>
			<v-alert v-if="captureError" dense outlined type="warning" class="trained-face-capture-panel__fallback mt-2">
				{{ captureError }}. Upload remains available as a fallback.
			</v-alert>
			<div class="d-flex flex-wrap justify-space-between align-center mt-2">
				<div class="mb-2 mb-sm-0">
					<v-btn
						x-small
						outlined
						:color="facingModePreference === 'user' ? 'primary' : undefined"
						class="mr-2"
						@click="setFacingModePreference('user')"
					>
						{{ t('trained_face_capture_facing_user', 'Front') }}
					</v-btn>
					<v-btn
						x-small
						outlined
						:color="facingModePreference === 'environment' ? 'primary' : undefined"
						@click="setFacingModePreference('environment')"
					>
						{{ t('trained_face_capture_facing_environment', 'Back') }}
					</v-btn>
				</div>
				<div>
				<v-btn x-small outlined color="secondary" class="mr-2" @click="startBrowserStream">
					<v-icon small left>mdi-refresh</v-icon>
					{{ t('trained_face_capture_start_preview', 'Start preview') }}
				</v-btn>
				<v-btn x-small color="primary" :disabled="!isPreviewReady" @click="captureBrowserFrame">
					<v-icon small left>mdi-camera</v-icon>
					{{ t('trained_face_capture_action', 'Capture') }}
				</v-btn>
				</div>
			</div>
		</div>

		<div v-else-if="source === 'registered-camera'" class="trained-face-capture-panel__registered-camera">
			<v-select
				dense
				outlined
				:items="registeredCameras"
				:item-text="registeredCameraText"
				item-value="deviceId"
				:value="selectedRegisteredCameraId"
				:loading="loadingRegisteredCameras"
				:label="t('trained_face_capture_registered', 'Registered camera')"
				@change="$emit('select-registered-camera', $event)"
			></v-select>

			<img
				v-if="registeredPreviewSource"
				ref="registeredPreview"
				class="trained-face-capture-panel__registered-preview"
				:src="registeredPreviewSource"
				alt="Registered camera preview"
			/>
			<v-alert v-else dense outlined :type="registeredStreamError ? 'warning' : 'info'" class="trained-face-capture-panel__fallback mt-2">
				{{ registeredStreamFallbackMessage }}
			</v-alert>
			<div class="d-flex justify-end mt-2">
				<v-btn x-small outlined color="secondary" class="mr-2" :loading="registeredStreamLoading" :disabled="!selectedRegisteredCameraId" @click="$emit('start-registered-stream')">
					<v-icon small left>mdi-refresh</v-icon>
					{{ t('trained_face_capture_start_preview', 'Start preview') }}
				</v-btn>
				<v-btn v-if="registeredStreamActive" x-small outlined color="error" class="mr-2" @click="$emit('stop-registered-stream')">
					<v-icon small left>mdi-stop</v-icon>
					{{ t('trained_face_capture_stop_preview', 'Stop preview') }}
				</v-btn>
				<v-btn x-small color="primary" :disabled="!registeredStreamActive" @click="$emit('capture-registered-frame')">
					<v-icon small left>mdi-camera</v-icon>
					{{ t('trained_face_capture_action', 'Capture') }}
				</v-btn>
			</div>
		</div>
	</v-card>
</template>

<script>
	export default {
		name: 'TrainedFaceCapturePanel',
		props: {
			source: {
				type: String,
				default: 'upload',
			},
			loading: {
				type: Boolean,
				default: false,
			},
			label: {
				type: String,
				default: 'Select face images',
			},
			registeredCameras: {
				type: Array,
				default: () => [],
			},
			selectedRegisteredCameraId: {
				type: [String, Number],
				default: null,
			},
			selectedRegisteredCamera: {
				type: Object,
				default: null,
			},
			loadingRegisteredCameras: {
				type: Boolean,
				default: false,
			},
			registeredStreamActive: {
				type: Boolean,
				default: false,
			},
			registeredStreamLoading: {
				type: Boolean,
				default: false,
			},
			registeredStreamPreview: {
				type: String,
				default: '',
			},
			registeredStreamError: {
				type: String,
				default: '',
			},
		},
		data: () => ({
			mediaStream: null,
			captureError: '',
			cameraNotice: '',
			facingModePreference: 'user',
		}),
		computed: {
			isBrowserCameraSource(){
				return this.source === 'browser-camera'
			},
			browserVideoConstraints(){
				return { facingMode: { ideal: this.facingModePreference } }
			},
			isPreviewReady(){
				return Boolean(this.mediaStream && this.mediaStream.active !== false)
			},
			registeredPreviewSource(){
				return this.registeredStreamPreview
			},
			registeredStreamFallbackMessage(){
				if (this.registeredStreamError) return this.registeredStreamError
				if (this.registeredStreamActive) return this.t('trained_face_capture_auth_preview_fallback', 'Authenticated live preview is not available in this browser yet. Capture still uses the secure backend snapshot.')
				return this.t('trained_face_capture_no_preview', 'No browser preview is available for this registered camera. Upload remains available as a fallback.')
			},
		},
		watch: {
			source: {
				immediate: true,
				handler(){
					this.stopBrowserStream()
					this.captureError = ''
					this.cameraNotice = this.isBrowserCameraSource ? this.getBrowserCameraReadinessMessage() : ''
				},
			},
		},
		methods: {
			t(key, fallback){
				return this.$ml && this.$ml.get ? this.$ml.get(key) : fallback
			},
			registeredCameraText(item){
				return item && (item.name || item.deviceId || item.id) ? (item.name || item.deviceId || item.id) : 'Camera'
			},
			setFacingModePreference(preference){
				this.facingModePreference = preference === 'environment' ? 'environment' : 'user'
			},
			isLocalhost(hostname){
				return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1'
			},
			isCameraSecureContext(){
				if (typeof window === 'undefined') return true
				const location = window.location || {}
				return Boolean(window.isSecureContext || location.protocol === 'https:' || this.isLocalhost(location.hostname))
			},
			getBrowserCameraReadinessMessage(){
				if (!this.isCameraSecureContext()) {
					return this.t('trained_face_capture_requires_secure_context', 'Camera access requires HTTPS or localhost. Open this page over HTTPS, use localhost during development, or upload an image instead.')
				}
				if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
					return this.t('trained_face_capture_unsupported', 'Camera access is not supported by this browser. Use a supported browser or upload an image instead.')
				}
				return ''
			},
			emitFiles(files){
				this.$emit('upload-files', Array.from(files || []));
			},
			handleDrop(event){
				const files = event && event.dataTransfer ? event.dataTransfer.files : [];
				this.emitFiles(files);
			},
			async startBrowserStream(){
				this.stopBrowserStream()
				this.captureError = ''
				this.cameraNotice = this.getBrowserCameraReadinessMessage()
				if (this.cameraNotice) {
					return false
				}

				try{
					const stream = await this.requestBrowserStream()
					await this.attachBrowserStream(stream)
					return true
				}catch(error){
					console.warn('Browser camera stream could not start', error)
					this.captureError = this.t('trained_face_capture_stream_unavailable', 'Camera stream could not start. Upload remains available as a fallback.')
					return false
				}
			},
			async requestBrowserStream(){
				try{
					return await navigator.mediaDevices.getUserMedia({ video: this.browserVideoConstraints })
				}catch(error){
					try{
						return await navigator.mediaDevices.getUserMedia({ video: true })
					}catch(fallbackError){
						throw fallbackError || error
					}
				}
			},
			async attachBrowserStream(stream){
				const video = this.$refs.video
				try{
					if (video) {
						video.srcObject = stream
						if (video.play) await video.play()
					}
					this.mediaStream = stream
					this.captureError = ''
					this.cameraNotice = ''
				}catch(error){
					if (stream && stream.getTracks) {
						stream.getTracks().forEach(track => {
							if (track && track.stop) track.stop()
						})
					}
					throw error
				}
			},
			stopBrowserStream(){
				if (this.mediaStream && this.mediaStream.getTracks) {
					this.mediaStream.getTracks().forEach(track => {
						if (track && track.stop) track.stop()
					})
				}
				this.mediaStream = null
				if (this.$refs.video) this.$refs.video.srcObject = null
			},
			createCaptureFile(blob, name){
				if (typeof File !== 'undefined') {
					return new File([blob], name, { type: blob.type || 'image/jpeg' })
				}
				blob.name = name
				return blob
			},
			canvasToFile(canvas, name){
				return new Promise((resolve, reject) => {
					canvas.toBlob(blob => {
						if (!blob) {
							reject(new Error('Unable to capture image'))
							return
						}
						resolve(this.createCaptureFile(blob, name))
					}, 'image/jpeg', 0.92)
				})
			},
			async captureBrowserFrame(){
				const video = this.$refs.video
				if (!video || !this.isPreviewReady) {
					this.captureError = this.t('trained_face_capture_not_ready', 'Camera preview is not ready')
					return false
				}

				try{
					const canvas = document.createElement('canvas')
					canvas.width = video.videoWidth || 640
					canvas.height = video.videoHeight || 480
					canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height)
					const file = await this.canvasToFile(canvas, 'browser-camera-capture.jpg')
					this.$emit('capture-file', file, this.source)
					return true
				}catch(error){
					console.warn('Browser camera frame capture failed', error)
					this.captureError = this.t('trained_face_capture_snapshot_failed', 'Snapshot failed. Try again or upload an image instead.')
					return false
				}
			},
			async captureRegisteredFrame(){
				const image = this.$refs.registeredPreview
				if (!image || !this.registeredPreviewUrl) return false

				try{
					const canvas = document.createElement('canvas')
					canvas.width = image.naturalWidth || image.width || 640
					canvas.height = image.naturalHeight || image.height || 480
					canvas.getContext('2d').drawImage(image, 0, 0, canvas.width, canvas.height)
					const file = await this.canvasToFile(canvas, 'registered-camera-capture.jpg')
					this.$emit('capture-file', file, 'registered-camera')
					return true
				}catch(error){
					console.warn('Registered camera frame capture failed', error)
					this.captureError = this.t('trained_face_capture_snapshot_failed', 'Snapshot failed. Try again or upload an image instead.')
					return false
				}
			},
		},
		beforeDestroy(){
			this.stopBrowserStream()
		},
	}
</script>

<style scoped>
	.trained-face-capture-panel__dropzone {
		border: 1px dashed var(--v-primary-base, #1976d2);
		border-radius: 8px;
		padding: 12px;
	}

	.trained-face-capture-panel__video,
	.trained-face-capture-panel__registered-preview {
		background: #101820;
		border-radius: 8px;
		display: block;
		min-height: 220px;
		object-fit: contain;
		width: 100%;
	}
</style>
